import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { captureException } from '@sentry/node';
import { useRouter } from 'next/router';

import { doRequest } from '@r1-frontend/do-request';
import { IRegPayCard } from '@r1-frontend/api-domru/api-pay/v1/card/dto/IRegPayCard';
import { makePaymentUnauth } from '@r1-frontend/api-domru/api-pay/v1/payment/add';
import { IRegisterPayment } from '@r1-frontend/api-domru/api-pay/v1/payment/dto/IRegisterPayment';

import { btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';

import { ApplePayBtn, TYPE_APPLE_TOKEN } from '~/src/components/ApplePayBtn';
import { GooglePayBtn } from '~/src/components/GooglePayBtn';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { getABNVariant } from '~/src/helpers/abn-tests';
import { TState } from '~/src/store';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import * as ST from './styled';

const DISPLAY_GOOGLE_BTN = 'display_google_btn';
const DISPLAY_APPLE_BTN = 'display_apple_btn';

interface IPaymentVariantsProps {
    paySumm: number,
    agreementNumber: number | null | undefined,
    isLastStep?: boolean,
}

export const PaymentVariants: React.FC<IPaymentVariantsProps> = ({
    paySumm,
    agreementNumber,
    isLastStep = false,
}): JSX.Element => {
    const analytics = useAnalytics();

    const [isGoogleBtnDisplay, setGoogleBtnDisplay] = useState(false);
    const [isAppleBtnDisplay, setAppleBtnDisplay] = useState(false);

    const router = useRouter();
    const { providerId, currentCity } = useSelector((state: TState) => ({
        providerId: selectProviderId(state),
        currentCity: state.city.currentCity,
    }));

    const onPaymentMake = useCallback(async(data: Partial<IRegisterPayment>) => {
        router.push({
            pathname:'/payments',
            query: {
                pay: 'pay_token',
                MD: data.gateTransactionId,
                PaReq: data.PaReq,
                TermUrl: data.TermUrl,
                acsUrl: data.acsUrl,
                billingTransactionId: data.billingTransactionId,
            },
        });
    }, [router]);

    const onPaymentCreate = useCallback(async(paymentToken, tokenType) => {
        if (!providerId && !agreementNumber && !paySumm) {
            return false;
        }

        try {
            const typeAggreement = 1;

            const token = tokenType === TYPE_APPLE_TOKEN
                ? window.btoa(JSON.stringify(paymentToken))
                : window.btoa(paymentToken);

            if (providerId && paymentToken) {
                const params = {
                    number_type: typeAggreement,
                    number_value: agreementNumber,
                    payment_token: token,
                    payment_token_type: tokenType,
                    payment_value: paySumm,
                    payment_source: 1,
                    return_url: location.origin + '/payments-response?',
                };

                const paymentResult = await doRequest.apiPay<IRegPayCard>(
                    makePaymentUnauth(providerId, params),
                );
                if (paymentResult.isSuccess) {
                    const data = paymentResult.payload;

                    onPaymentMake(
                        data?.regStatus !== 0 ? data : {},
                    );
                }
            }
        } catch (err) {
            captureException(err);
        }
    }, [currentCity, providerId, paySumm, agreementNumber, onPaymentMake]);

    useEffect(() => {
        const abnGoogleVariant = getABNVariant(DISPLAY_GOOGLE_BTN);

        const abnAppleVariant = getABNVariant(DISPLAY_APPLE_BTN);

        if (abnGoogleVariant === 'display') {
            setGoogleBtnDisplay(true);
        }

        if (abnAppleVariant === 'display') {
            setAppleBtnDisplay(true);
        }
    }, []);

    const onGoToPayment = useCallback(() => {
        analytics.final.clickPayment();
        router.push({
            pathname:'/payments',
            query: {
                pay: 'online',
                agreement: agreementNumber,
                s: paySumm,
            },
        });
    }, []);

    return (
        <>
        {paySumm &&
            <ST.PaymentVariants>
                {isGoogleBtnDisplay &&
                    <GooglePayBtn
                        paySumm={paySumm}
                        onPaymentCreate={onPaymentCreate}
                    />
                }
                {isAppleBtnDisplay &&
                    <ApplePayBtn
                        currentCity={currentCity}
                        paySumm={paySumm}
                        onPaymentCreate={onPaymentCreate}
                    />
                }
                <ST.Button
                    text={isGoogleBtnDisplay || isAppleBtnDisplay
                        ? 'Другие способы оплаты'
                        : 'Оплатить'
                    }
                    onClick={onGoToPayment}
                    type={isLastStep ? btnTypes.ACCENT : btnTypes.PRIMARY}
                />
            </ST.PaymentVariants>
        }
        </>
    );
};
